import {
  CheckOutlined,
  ThunderboltFilled,
  MergeOutlined,
  SignatureOutlined,
  WarningOutlined,
  PlusOutlined,
  ArrowDownOutlined,
  IssuesCloseOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CrossStationArrowIcon, crossStationLabelOf } from "./crossStation";
import StationPartTag from "./StationPartTag";
import { isCommentModifier } from "../types";
import { LunchTag } from "../../ordering/lunch/LunchTag";

interface ProductItemProps {
  productName: string;
  // Ред от обедно меню: етикет „Обедно" + обедният размер (ако е сменен) —
  // решетката иначе не показва размер, но обедният е различен от обичайния
  isLunch?: boolean;
  lunchUnitAndSize?: string | null;
  quantity: number;
  completedQuantity?: number;
  status: "0" | "1" | "2" | "3";
  isSplit?: boolean;
  isCancelled?: boolean;
  showOrderHeader?: boolean;
  orderTableName?: string;
  orderOldTableName?: string | null;
  orderWaiterName?: string;
  orderTime?: string;
  orderComment?: string;
  elapsedTime?: string;
  isOrderCompleted?: boolean;
  backgroundColor?: string;
  isSelected?: boolean;
  onClick?: () => void;
  onHeaderClick?: () => void;
  modifiers?: { id: string; name: string }[];
  hasAdditions?: boolean;
  isAddition?: boolean;
  // При добавка без видим основен продукт (той е на друга станция) —
  // подтекст „(към Бургер от Топла Кухня)" под името.
  mainProductName?: string | null;
  mainStationName?: string | null;
  // Част от продукт на няколко станции: чия е (само при монитор с няколко
  // станции — виж stationParts.ts).
  stationTag?: string | null;
  // „+ Студена кухня · готово ✓" / при шпикер „Скара ✓ · Студена кухня …".
  stationHint?: string | null;
  shouldEnhanceBackground?: boolean;
  aggregatedTotal?: number;
  isOverdue?: boolean;
  // Кои статуси значат „изпълнено" на този екран ("2" обикновен, "3" шпикер).
  fulfilledStatuses?: string[];
  // Шпикер с „всички статуси": иконка и за "0", за да се различава от 1/2
  // (иначе в решетката незапочнатото е нарочно без иконка).
  showUnstartedIcon?: boolean;
}

const ProductItem = ({
  productName,
  isLunch = false,
  lunchUnitAndSize = null,
  quantity,
  completedQuantity = 0,
  status,
  isSplit = false,
  isCancelled = false,
  showOrderHeader,
  orderTableName,
  orderOldTableName,
  orderWaiterName,
  orderTime,
  orderComment,
  elapsedTime,
  isOrderCompleted = false,
  backgroundColor,
  isSelected = false,
  onClick,
  onHeaderClick,
  modifiers,
  hasAdditions = false,
  isAddition = false,
  mainProductName,
  mainStationName,
  stationTag = null,
  stationHint = null,
  shouldEnhanceBackground = false,
  aggregatedTotal,
  isOverdue = false,
  fulfilledStatuses = ["2"],
  showUnstartedIcon = false,
}: ProductItemProps) => {
  const { t } = useTranslation();

  const crossStationLabel = crossStationLabelOf(
    t,
    isAddition,
    mainProductName,
    mainStationName,
  );

  const isFullyCompleted =
    fulfilledStatuses.includes(status) ||
    (quantity > 1 && completedQuantity >= quantity);
  const showProgress =
    quantity > 1 && completedQuantity > 0 && completedQuantity < quantity;

  const hasModifiers = !!modifiers && modifiers.length > 0;
  const modifiersHeight = hasModifiers ? modifiers!.length * 26 + 8 : 0;

  // Чисто визуален tint — не променя избора на иконка/статус.
  const tintOverdue = isOverdue && !isSplit && !isCancelled;

  const getStatusIcon = () => {
    if (isSplit) return <MergeOutlined className="text-[18px] text-muted" />;
    if (isCancelled)
      return <SignatureOutlined className="text-[18px] text-muted" />;
    // Издадено от шпикера — синя „изпратено" иконка. Преди общия зелен чек,
    // за да се различава от „сготвено" в таб „Изпълнени" на шпикера.
    if (status === "3")
      return <SendOutlined className="text-[19px] text-info" />;
    if (isFullyCompleted)
      return (
        <CheckOutlined
          className={
            tintOverdue
              ? "text-[19px] text-danger-ink"
              : "text-[19px] text-ok"
          }
        />
      );
    switch (status) {
      // Незапочнат продукт ("0") — БЕЗ иконка (заявка на кухнята); кутийката
      // остава празна, за да не скача името. Изключение: шпикер с „всички
      // статуси" — там иконката отличава незапочнато от в процес/сготвено.
      case "0":
        return showUnstartedIcon ? (
          <IssuesCloseOutlined
            className={
              tintOverdue
                ? "text-[18px] text-danger-ink"
                : "text-[18px] text-muted"
            }
          />
        ) : null;
      case "1":
        return (
          <ThunderboltFilled
            className={
              tintOverdue
                ? "text-[19px] text-danger-ink"
                : "text-[19px] text-brand-ink"
            }
          />
        );
      case "2":
        return (
          <CheckOutlined
            className={
              tintOverdue
                ? "text-[19px] text-danger-ink"
                : "text-[19px] text-ok"
            }
          />
        );
      default:
        return null;
    }
  };

  // Засилваме цвета за групирани основни+добавки редове
  const enhanceColor = (color: string | undefined): string | undefined => {
    if (!color || !shouldEnhanceBackground) return color;
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const factor = 0.9;
    const newR = Math.max(0, Math.floor(r * factor));
    const newG = Math.max(0, Math.floor(g * factor));
    const newB = Math.max(0, Math.floor(b * factor));
    return `#${newR.toString(16).padStart(2, "0")}${newG
      .toString(16)
      .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
  };

  const enhancedBackgroundColor = enhanceColor(backgroundColor);
  const isInert = isSplit || isCancelled;
  // При незапочнат продукт няма иконка → не рендираме и кутийката ѝ, за да
  // започва името по-отляво и текстът да има повече място.
  const statusIcon = getStatusIcon();
  const struck = isInert ? "line-through" : "";

  return (
    <div>
      {showOrderHeader && (
        <div
          onClick={(clickEvent) => {
            // Не стига до фона (той сваля селекцията)
            clickEvent.stopPropagation();
            onHeaderClick?.();
          }}
          // Средно сива заглавна лента — по-тъмна от редовете, но не
          // агресивна; заедно с рамката и сянката на картата (сглобява се в
          // MonitorsGridView — една карта = една поръчка) отсича поръчките.
          className={`flex h-[56px] items-center justify-between gap-[12px] overflow-hidden border-b border-shade-3 bg-shade-3 px-[12px] ${
            onHeaderClick ? "cursor-pointer" : "cursor-default"
          }`}
        >
          <div className="flex min-w-0 flex-1 flex-col leading-[1.15]">
            <span className="truncate text-[18px] font-bold tracking-[-0.3px] text-ink">
              {orderOldTableName
                ? `#${orderOldTableName} » ${orderTableName}`
                : `#${orderTableName}`}
            </span>
            {orderComment && (
              <span className="mt-[2px] truncate text-[14.5px] font-medium italic text-body">
                {orderComment}
              </span>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end leading-[1.15]">
            <span className="whitespace-nowrap text-[15px] font-bold tracking-[-0.1px] text-ink">
              {orderWaiterName}
            </span>
            <span className="mt-[2px] whitespace-nowrap font-['JetBrains_Mono',ui-monospace,monospace] text-[13px] font-medium tracking-[0.3px] tabular-nums text-body">
              {orderTime}
              {!isOrderCompleted && elapsedTime && (
                <span
                  className={`ml-[5px] ${
                    tintOverdue ? "font-bold text-danger-ink" : ""
                  }`}
                >
                  ({elapsedTime})
                </span>
              )}
            </span>
          </div>
        </div>
      )}
      <div
        onClick={(clickEvent) => {
          // Не стига до фона (той сваля селекцията); inert редът не селектира
          clickEvent.stopPropagation();
          onClick?.();
        }}
        className={`flex flex-col border-b border-b-shade-2 border-l-[3px] transition-[background,border-color] duration-[120ms] ${
          isSelected ? "border-l-brand" : "border-l-transparent"
        } ${isInert ? "cursor-default opacity-50" : "cursor-pointer"}`}
        // Височината расте с броя модификатори, а фонът на неселектирана карта
        // идва от данните (цвят по маса) — стойности по време на изпълнение
        style={{
          height: hasModifiers ? 56 + modifiersHeight : 56,
          background: isSelected ? "var(--color-brand-tint)" : enhancedBackgroundColor,
        }}
      >
        <div className="flex h-[56px] items-center gap-[10px] px-[12px]">
          {statusIcon && (
            <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center">
              {statusIcon}
            </div>
          )}
          <span
            className={`flex flex-1 items-center gap-[6px] overflow-hidden break-words text-[17px] font-semibold leading-[1.2] tracking-[-0.15px] ${
              isSelected ? "text-brand-deep-ink" : "text-ink"
            } ${struck}`}
          >
            {hasAdditions && (
              <ArrowDownOutlined className="shrink-0 text-[13px] text-ink" />
            )}
            {isAddition && (
              <PlusOutlined className="shrink-0 text-[13px] text-ink" />
            )}
            <span className="min-w-0 line-clamp-2">
              {productName}
              {isLunch && lunchUnitAndSize && (
                <span className="ml-[6px] text-[14px] font-medium tabular-nums text-muted">
                  {lunchUnitAndSize}
                </span>
              )}
              {/* „({общо})" само когато НАИСТИНА има събиране от няколко реда —
                  единичен ред би дублирал количеството си като шум „(1)" */}
              {aggregatedTotal != null && aggregatedTotal > quantity && !isInert && (
                <span className="ml-[6px] text-[14px] font-medium tabular-nums text-muted">
                  ({aggregatedTotal})
                </span>
              )}
            </span>
            {stationTag && <StationPartTag name={stationTag} />}
          </span>
          {/* „Обедно" в своя колона пред количеството — подравнено по редовете */}
          {isLunch && <LunchTag />}
          <span
            className={`min-w-[62px] shrink-0 text-right text-[16.5px] font-bold tracking-[-0.1px] tabular-nums ${
              isSelected ? "text-brand-deep-ink" : "text-ink"
            } ${struck}`}
          >
            {showProgress
              ? `${completedQuantity} / ${quantity} ${t("monitors.pcs")}`
              : `${quantity} ${t("monitors.pcs")}`}
          </span>
        </div>

        {(crossStationLabel ?? stationHint) && (
          <div
            className={`flex items-center gap-[5px] pb-[6px] pr-[12px] ${
              statusIcon ? "pl-[46px]" : "pl-[12px]"
            }`}
          >
            <CrossStationArrowIcon />
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-medium leading-[18px] text-muted">
              {crossStationLabel ?? stationHint}
            </span>
          </div>
        )}

        {hasModifiers && (
          <div
            className={`flex flex-col pb-[8px] pr-[12px] ${
              // Подравняване с името: с иконка → 12+24+10; без → само падинга
              statusIcon ? "pl-[46px]" : "pl-[12px]"
            }`}
          >
            {modifiers!.map((mod) => (
              <div
                key={mod.id}
                className={`flex h-[26px] items-center gap-[9px] overflow-hidden text-[15.5px] font-medium text-ink ${
                  isCommentModifier(mod.id) ? "italic" : ""
                }`}
              >
                <WarningOutlined className="shrink-0 text-[14px] text-danger-ink" />
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {mod.name}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductItem;
